/**
 * Where the camera names outlive a page load: a camera's UUID must never surface in the UI, so
 * a label needs a name before `/devices` answers, and for a camera that is gone. Names change
 * rarely, so the last one listed is the right placeholder.
 *
 * A storage that refuses — private mode, quota — costs the names their next page load and
 * nothing else: no caller sees a throw.
 */

import type { CameraNames } from '@/types/devices';

const STORAGE_KEY = 'vms.cameraNames.v1';

export function readStoredCameraNames(): CameraNames {
    try {
        const storedValue: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
        return keepNames(storedValue) ? storedValue : {};
    } catch {
        return {};
    }
}

/** Drops each entry that is not a name from the parsed object itself, which nothing else holds. */
function keepNames(storedValue: unknown): storedValue is CameraNames {
    if (storedValue === null || typeof storedValue !== 'object' || Array.isArray(storedValue)) {
        return false;
    }
    for (const [id, name] of Object.entries(storedValue)) {
        if (typeof name !== 'string' || name === '') {
            Reflect.deleteProperty(storedValue, id);
        }
    }
    return true;
}

export function writeStoredCameraNames(names: CameraNames): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(names));
    } catch {
        // The store keeps its copy for this page load.
    }
}

/** Camera ids are an organization's own, so its names mean nothing under the next one. */
export function clearStoredCameraNames(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing was stored, or storage is unavailable; either way nothing remains to read.
    }
}
